import { resolveFieldLabel } from "@repo/shared";
import type { FinancialEntryItem, FinancialFieldDefinition } from "@repo/shared/types";
import { useMemo } from "react";
import { useT } from "@/lib/i18n/use-t";
import { EntryList } from "./entry-list";
import { T } from "./liabilities-tab.i18n";
import { TypeSummary } from "./type-summary";

type LiabilitiesTabProps = {
  entries: FinancialEntryItem[];
  fieldDefs: FinancialFieldDefinition[];
  /** QL-39 — see EntryList. */
  requiredTypeIds?: ReadonlySet<string>;
  missingRequiredIds?: ReadonlySet<string>;
  disabled: boolean;
  currency: string;
  lang: string;
  totalLiabilities: number;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<FinancialEntryItem>) => void;
};

export function LiabilitiesTab({
  entries,
  fieldDefs,
  requiredTypeIds,
  missingRequiredIds,
  disabled,
  currency,
  lang,
  totalLiabilities,
  onAdd,
  onRemove,
  onUpdate,
}: LiabilitiesTabProps) {
  const t = useT(T);

  const hasMonthlyPaymentForType = (typeId: string) => {
    const field = fieldDefs.find((f) => f.id === typeId);
    return field?.hasMonthlyPayment === true;
  };

  // QL-586 — the model's current label, not the one stored when the row was created.
  const labelFor = (entry: FinancialEntryItem) => {
    const def = fieldDefs.find((d) => d.id === entry.typeId);
    return (def ? resolveFieldLabel(def, lang) : "") || entry.typeLabel;
  };

  const summaryByType = useMemo(() => {
    const grouped: Record<string, { label: string; total: number; monthlyTotal: number }> = {};
    for (const entry of entries) {
      if (!grouped[entry.typeId]) {
        grouped[entry.typeId] = { label: labelFor(entry), total: 0, monthlyTotal: 0 };
      }
      grouped[entry.typeId].total += entry.amount || 0;
      grouped[entry.typeId].monthlyTotal += entry.monthlyPayment || 0;
    }
    return grouped;
  }, [entries, fieldDefs, lang]);

  return (
    <div className="space-y-4">
      <EntryList
        entries={entries}
        fieldDefs={fieldDefs}
        requiredTypeIds={requiredTypeIds}
        missingRequiredIds={missingRequiredIds}
        fieldLabels={t.fieldLabels}
        disabled={disabled}
        lang={lang}
        showMonthlyPayment
        addButtonLabel={t.addLiability}
        emptyMessage={t.noItemsYet}
        onAdd={onAdd}
        onRemove={onRemove}
        onUpdate={onUpdate}
        hasMonthlyPaymentForType={hasMonthlyPaymentForType}
      />

      <TypeSummary
        summary={summaryByType}
        total={totalLiabilities}
        showMonthly
        currency={currency}
        lang={lang}
      />
    </div>
  );
}
